import React, { useId } from 'react';
import { Box, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Select, TextField, useMediaQuery } from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { todayDialog } from '../../theme/todayDialogStyles';

/**
 * Base dialog in the Today design (serif title, mono labels, sans body).
 * Children should use the parts below or `Box` with explicit font sizes: MUI `Typography`/`Chip`
 * inherit the global 20px app font inside dialogs.
 */
export default function New26Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footerNote,
  primaryAction,
  secondaryAction = { label: 'Cancel' },
  maxWidth = 'sm',
  error,
  busy = false,
  overlay,
}) {
  const { isDarkMode } = useTheme();
  const fullScreen = useMediaQuery('(max-width:600px)');
  const td = todayDialog(isDarkMode, { fullScreen });
  const titleId = useId();
  const actionBusy = busy || !!primaryAction?.loading;
  const close = actionBusy ? undefined : onClose;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      disableEnforceFocus
      PaperProps={{ sx: { ...td.paper, fontFamily: td.SANS } }}
    >
      {overlay && (
        <Box role="status" sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 3, bgcolor: isDarkMode ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.92)' }}>
          {overlay}
        </Box>
      )}
      <DialogTitle component="div" sx={{ ...td.title, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h2" id={titleId} sx={td.h2}>{title}</Box>
          {subtitle && <Box component="p" sx={{ ...td.body, m: 0, mt: 0.75 }}>{subtitle}</Box>}
        </Box>
        <IconButton onClick={onClose} disabled={actionBusy} aria-label="Close" sx={td.closeBtn}>
          <FaTimes size={14} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, md: 3 }, py: 3, bgcolor: td.tk.surface }}>
        {error && (
          <Box role="alert" sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: '10px', border: `1px solid ${td.tk.rust}55`, color: td.tk.rust, fontSize: 14, fontWeight: 500 }}>
            {error}
          </Box>
        )}
        {children}
      </DialogContent>

      {(primaryAction || footerNote) && (
        <DialogActions sx={{ ...td.actions, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box component="span" sx={{ fontSize: 13, color: td.tk.muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {footerNote}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
            {secondaryAction && (
              <ButtonBase onClick={secondaryAction.onClick || onClose} disabled={actionBusy} sx={td.outlineBtn}>{secondaryAction.label}</ButtonBase>
            )}
            {primaryAction && (
              <ButtonBase onClick={primaryAction.onClick} disabled={primaryAction.disabled || primaryAction.loading} sx={primaryAction.danger ? td.rustBtn : td.primaryBtn}>
                {primaryAction.loading ? primaryAction.loadingLabel || 'Working…' : primaryAction.label}
              </ButtonBase>
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}

/** Mono uppercase section label. Pass `htmlFor` to label a field. */
export function DialogLabel({ children, htmlFor, id, sx }) {
  const { isDarkMode } = useTheme();
  const td = todayDialog(isDarkMode);
  return (
    <Box component={htmlFor ? 'label' : 'span'} htmlFor={htmlFor} id={id} sx={{ ...td.eyebrow, mt: 3, mb: 1.25, ...sx }}>
      {children}
    </Box>
  );
}

function ChoiceList({ multiple, label, options, isOn, onToggle, emptyTitle, emptyText }) {
  const { isDarkMode } = useTheme();
  const { tk, SANS, MONO } = todayDialog(isDarkMode);
  const labelId = useId();
  const toneColor = { ok: tk.green, soon: tk.amberText, urgent: tk.rust };

  return (
    <>
      {label && <DialogLabel id={labelId}>{label}</DialogLabel>}
      {options.length === 0 ? (
        <Box sx={{ p: 2.5, borderRadius: '14px', border: `1px dashed ${tk.lineStrong}`, textAlign: 'center' }}>
          {emptyTitle && <Box component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 600, color: tk.ink }}>{emptyTitle}</Box>}
          {emptyText && <Box component="span" sx={{ display: 'block', fontSize: 13, color: tk.muted, mt: 0.5 }}>{emptyText}</Box>}
        </Box>
      ) : (
        <Box role={multiple ? 'group' : 'radiogroup'} aria-labelledby={label ? labelId : undefined}>
          {options.map((o, i) => {
            const on = isOn(o.value);
            return (
              <ButtonBase
                key={o.key || o.value}
                role={multiple ? 'checkbox' : 'radio'}
                aria-checked={on}
                onClick={() => onToggle(o.value)}
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '20px minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 1.75,
                  py: 1.75,
                  textAlign: 'left',
                  fontFamily: SANS,
                  borderTop: i ? `1px solid ${tk.line}` : 0,
                  '&:hover .today-mark': { borderColor: tk.navy },
                  '&.Mui-focusVisible': { outline: `2px solid ${tk.navy}`, outlineOffset: '2px' },
                }}
              >
                <Box component="span" aria-hidden className="today-mark" sx={{ width: 18, height: 18, borderRadius: multiple ? '5px' : '50%', boxSizing: 'border-box', border: `2px solid ${on ? tk.navy : tk.lineStrong}`, bgcolor: multiple && on ? tk.navy : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDarkMode ? tk.panel : '#fff' }}>
                  {on && (multiple
                    ? <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.5l2.5 2.5 4.5-5.5" /></svg>
                    : <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tk.navy }} />)}
                </Box>
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                  <Box component="span" sx={{ fontSize: 15, fontWeight: 600, color: tk.ink }}>{o.title}</Box>
                  {o.description && (
                    <Box component="span" sx={{ fontSize: 13, color: tk.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.description}</Box>
                  )}
                </Box>
                {o.tag && <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: toneColor[o.tone] || tk.muted }}>{o.tag}</Box>}
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </>
  );
}

/**
 * Single-choice list: radio dot, title + description, mono tag on the right, line between rows.
 * Options: `{ value, title, description?, tag?, tone?: 'ok' | 'soon' | 'urgent' }`.
 */
export function DialogRadioList({ value, onChange, ...props }) {
  return <ChoiceList {...props} isOn={(v) => v === value} onToggle={onChange} />;
}

/** Multi-choice version of DialogRadioList; `value` is an array, `onChange` gets the next array. */
export function DialogCheckList({ value = [], onChange, ...props }) {
  return (
    <ChoiceList
      {...props}
      multiple
      isOn={(v) => value.includes(v)}
      onToggle={(v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])}
    />
  );
}

/** Text input styled for Today dialogs; accepts any MUI TextField props. */
export function DialogTextField({ sx, ...props }) {
  const { isDarkMode } = useTheme();
  const { tk, SANS } = todayDialog(isDarkMode);
  return (
    <TextField
      fullWidth
      {...props}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px',
          bgcolor: tk.surfaceAlt,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tk.lineStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tk.navy },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tk.navy, borderWidth: 1 },
        },
        '& .MuiOutlinedInput-input': { fontFamily: SANS, fontSize: 15, color: tk.ink, py: 1.5 },
        ...sx,
      }}
    />
  );
}

/** Select for use inside New26Dialog. Menu sits above the dialog so options are clickable. */
export function DialogSelect({ children, sx, MenuProps, ...props }) {
  const { isDarkMode } = useTheme();
  const { tk, SANS } = todayDialog(isDarkMode);
  return (
    <Select
      fullWidth
      displayEmpty
      {...props}
      MenuProps={{
        ...MenuProps,
        disablePortal: false,
        style: { zIndex: 1600, ...MenuProps?.style },
        sx: { zIndex: 1600, ...MenuProps?.sx },
        PaperProps: {
          ...MenuProps?.PaperProps,
          sx: {
            maxHeight: 420,
            mt: 1,
            bgcolor: tk.surface,
            color: tk.ink,
            border: `1px solid ${tk.line}`,
            borderRadius: '14px',
            boxShadow: isDarkMode ? '0 12px 40px rgba(0, 0, 0, 0.45)' : '0 12px 40px rgba(21, 23, 28, 0.12)',
            backgroundImage: 'none',
            ...MenuProps?.PaperProps?.sx,
          },
        },
        MenuListProps: { sx: { py: 1, px: 0.75, fontFamily: SANS }, ...MenuProps?.MenuListProps },
      }}
      sx={{
        borderRadius: '10px',
        bgcolor: tk.surfaceAlt,
        fontFamily: SANS,
        '& .MuiSelect-select': { py: 1.5, fontFamily: SANS, fontSize: 15, color: tk.ink },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: tk.lineStrong },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tk.navy },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tk.navy, borderWidth: 1 },
        ...sx,
      }}
    >
      {children}
    </Select>
  );
}
